type ImageOptions = {
  className?: string;
  display?: 'standard' | 'full-bleed';
  height?: number;
  pad?: boolean;
  quality?: number;
  retainRatio?: boolean;
  width?: number;
};

type Width =
  | 72
  | 150
  | 200
  | 250
  | 300
  | 350
  | 357
  | 366
  | 380
  | 400
  | 414
  | 450
  | 474
  | 490
  | 576
  | 627
  | 672
  | 720
  | 768
  | 800
  | 900
  | 1000
  | 1024
  | 1160
  | 1440;

type Height =
  | 72
  | 150
  | 200
  | 250
  | 300
  | 350
  | 378
  | 380
  | 400
  | 414
  | 450
  | 474
  | 576
  | 653
  | 672
  | 768
  | 800
  | 1000
  | 1024
  | 1160;

type ResponsiveImageOptions = {
  height?: Height;
  pad?: boolean;
  quality?: number;
  retainRatio?: boolean;
  src?: string;
  width?: Width;
};

type ResponsiveImages = {
  lg?: ResponsiveImageOptions;
  md?: ResponsiveImageOptions;
  sm?: ResponsiveImageOptions;
  xl?: ResponsiveImageOptions;
  xs?: ResponsiveImageOptions;
};

type Props = {
  alt: string | undefined;
  caption?: string;
  loading?: 'lazy' | 'eager';
  responsiveOptions?: ResponsiveImages;
  src: string;
} & ImageOptions;

export const smallImageOptions = ({
  pad
}: {
  pad: boolean;
}): Partial<Props> => {
  return {
    height: 300,
    pad,
    responsiveOptions: {
      lg: { height: 400, pad, width: 400 },
      sm: { height: 300, pad, width: 300 }
    },
    width: 300
  };
};

export function addResizerFlags(
  src: string,
  { width, height, quality, pad, retainRatio }: ImageOptions
) {
  if (!src) return;

  const flags = [];

  if (width) flags.push(`_w${width}`);
  if (height) flags.push(`_h${height}`);
  if (quality) flags.push(`_q${quality}`);
  if (pad) flags.push('_pad_');
  if (retainRatio) flags.push('_rr_');

  const extension = src.match(/\.[^.]+$/);

  return src.replace(`${extension}`, `${flags.join('')}${extension}.webp`);
}

const ResponsiveSource = ({
  src,
  minWidth,
  options
}: {
  minWidth: string;
  options?: ImageOptions;
  src: string;
}) => {
  if (!options) return null;

  return (
    <source
      srcSet={addResizerFlags(src, options)}
      media={`(min-width: ${minWidth})`}
      height={options.height}
      width={options.width}
      className="h-auto max-w-full"
    />
  );
};

const Image = ({
  display,
  caption,
  className = '',
  src,
  responsiveOptions,
  alt,
  loading,
  height,
  pad,
  quality,
  width
}: Props) => {
  const isStandardDisplay = display === 'standard';

  return (
    <div
      className={`flex flex-col ${className} ${
        isStandardDisplay ? 'max-w-2xl' : ''
      }`}
    >
      <picture className={`block ${className}`}>
        <ResponsiveSource
          src={responsiveOptions?.xl?.src ?? src}
          minWidth="1280px"
          options={responsiveOptions?.xl}
        />
        <ResponsiveSource
          src={responsiveOptions?.lg?.src ?? src}
          minWidth="1024px"
          options={responsiveOptions?.lg}
        />
        <ResponsiveSource
          src={responsiveOptions?.md?.src ?? src}
          minWidth="768px"
          options={responsiveOptions?.md}
        />
        <ResponsiveSource
          src={responsiveOptions?.sm?.src ?? src}
          minWidth="640px"
          options={responsiveOptions?.sm}
        />
        <ResponsiveSource
          src={responsiveOptions?.xs?.src ?? src}
          minWidth="200px"
          options={responsiveOptions?.xs}
        />
        <img
          className={className}
          loading={loading}
          src={addResizerFlags(src, { height, pad, quality, width })}
          alt={alt}
        />
      </picture>
      {Boolean(caption) && (
        <div className="relative">
          <div className="absolute -bottom-5 right-1 text-xs font-semibold text-light-grey">
            {caption}
          </div>
        </div>
      )}
    </div>
  );
};

export default Image;
